import { AlertCircleIcon, Building2Icon, PlusIcon } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';

import { PermissionsGate } from '@/modules/auth/components/permissions/PermissionsGate';
import { permissions } from '@/modules/auth/permissions';
import {
  companyClientStatusOptions,
  normalizeCompanyClientStatuses,
  parseCompanyClientStatuses,
} from '@/modules/companies/clientStatus';
import { CompanyList } from '@/modules/companies/components/CompanyList';
import { useCompaniesQuery } from '@/modules/companies/queries/useCompaniesQuery';
import { Alert, AlertDescription } from '@/modules/ui/components/alert/Alert';
import { Button } from '@/modules/ui/components/button/Button';
import Container from '@/modules/ui/components/container/Container';
import { EmptyState } from '@/modules/ui/components/empty-state/EmptyState';
import Loader from '@/modules/ui/components/loader/Loader';
import { PageHeader } from '@/modules/ui/components/page-header/PageHeader';
import { MultiSelect } from '@/modules/ui/MultiSelect';

const Companies = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedClientStatuses = parseCompanyClientStatuses(
    searchParams.get('clientStatuses'),
  );
  const companiesQuery = useCompaniesQuery({
    filters: selectedClientStatuses
      ? { clientStatuses: selectedClientStatuses }
      : undefined,
  });

  const handleClientStatusesChange = (values: string[]) => {
    const statuses = normalizeCompanyClientStatuses(values);
    const newSearchParams = new URLSearchParams(searchParams);

    if (statuses) {
      newSearchParams.set('clientStatuses', statuses.join(','));
    } else {
      newSearchParams.delete('clientStatuses');
    }

    setSearchParams(newSearchParams);
  };

  const hasNoMatchingCompanies =
    !companiesQuery.isLoading &&
    !companiesQuery.isError &&
    selectedClientStatuses !== undefined &&
    (companiesQuery.data?.length ?? 0) === 0;

  return (
    <Container>
      <PageHeader
        title="Companies"
        action={
          <PermissionsGate permissions={[permissions.Companies.Create]}>
            <Button variant="primary" asChild>
              <Link to="/companies/add">
                <PlusIcon />
                Add Company
              </Link>
            </Button>
          </PermissionsGate>
        }
      />

      <div className="mb-6 flex flex-wrap items-center gap-3">
        <MultiSelect
          options={companyClientStatusOptions}
          defaultValue={selectedClientStatuses}
          onValueChange={handleClientStatusesChange}
          placeholder="Filter by client status..."
          maxCount={1}
          searchable={false}
          hideSelectAll
          className="w-full sm:w-[220px]"
        />
      </div>

      {companiesQuery.isError && (
        <Alert variant="destructive">
          <AlertCircleIcon />
          <AlertDescription>Failed to load companies</AlertDescription>
        </Alert>
      )}
      {companiesQuery.isLoading && <Loader />}
      {hasNoMatchingCompanies ? (
        <EmptyState
          icon={<Building2Icon className="size-12" />}
          title="No companies found"
          description="No companies match the selected client statuses."
        />
      ) : (
        !companiesQuery.isLoading &&
        !companiesQuery.isError && (
          <CompanyList companies={companiesQuery.data ?? []} />
        )
      )}
    </Container>
  );
};

export default Companies;
